import React, { useEffect } from 'react';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { Tooltip } from 'antd';
import type { ButtonProps } from 'antd/es/button/button';

import { useSimpleModal } from '../../Common/Modal/Simple';
import { useMessageError, useMessageSuccess } from '../../../hooks/common';
import { listAction } from '../../Common/Table';
import {
  useTariffDelete,
  type TariffTableRow,
} from '../../../hooks/api/tariffs';

interface ActionButtonProps<RequestRes = undefined> {
  record: TariffTableRow;
  buttonProps?: ButtonProps;
  setTableKey?: (newVal: number) => void;
  afterSuccess?: (res?: RequestRes) => void;
}

export default function DeleteTariffButton({
  record,
  setTableKey,
  afterSuccess,
  buttonProps,
}: ActionButtonProps) {
  const tariffDelete = useTariffDelete(record.id);
  const { open, contextHolder } = useSimpleModal();

  useMessageError([tariffDelete]);
  useMessageSuccess([tariffDelete], 'Tariff deleted successfully');

  useEffect(() => {
    if (!tariffDelete.error && !tariffDelete.loading && tariffDelete.data) {
      setTableKey?.(Date.now());
      afterSuccess?.();
    }
  }, [tariffDelete.error, tariffDelete.loading, tariffDelete.data, setTableKey, afterSuccess]);

  const ActionButton = listAction.delete;

  return (
    <>
      <Tooltip title="Delete tariff" placement="topRight">
        <ActionButton
          loading={tariffDelete.loading}
          title="Delete tariff"
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();
            open({
              icon: <ExclamationCircleFilled />,
              title: 'Delete tariff?',
              content: (
                <span>
                  Are you sure you want to delete tariff
                  {' '}
                  <b>{record.name || '-'}</b>
                  ?
                </span>
              ),
              cancelText: 'Cancel',
              okText: 'Delete',
              okButtonProps: { danger: true },
              onOk: () => tariffDelete.fetch(),
            });
          }}
          {...buttonProps}
        />
      </Tooltip>
      {contextHolder}
    </>
  );
}
